import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSET_STATUSES,
  ASSET_STATUS_MAP,
  DEPRECIATION_METHODS,
  DEPRECIATION_METHOD_MAP,
  type AssetStatus,
  type DepreciationMethod,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { ExportMenu } from '../../components/ExportMenu';
import {
  exportDay,
  exportNotes,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface AssetRow {
  id: string;
  code: string;
  name: string;
  serialNumber: string | null;
  location: string | null;
  status: AssetStatus;
  purchaseDate: string;
  purchaseCost: number;
  accumulatedDepreciation: number;
  bookValue: number;
  monthlyDepreciation: number;
  category: { id: string; name: string };
  custodian: { id: string; name: string } | null;
}

/**
 * أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في assets.service.ts.
 *
 * القيمة الدفترية محسوبة لا مخزّنة، فلا فلتر عليها.
 */
const ASSET_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الأصل', kind: 'text', sortable: true },
  { key: 'category', label: 'التصنيف', kind: 'text' },
  { key: 'custodian', label: 'في عهدة', kind: 'text' },
  { key: 'purchaseCost', label: 'التكلفة', kind: 'number', sortable: true },
  {
    key: 'accumulatedDepreciation',
    label: 'مجمع الإهلاك',
    kind: 'number',
    sortable: true,
  },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: ASSET_STATUSES.map((st) => ({ value: st, label: ASSET_STATUS_MAP[st].label })),
  },
];

interface AssetPage {
  items: AssetRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

interface Summary {
  count: number;
  retired: number;
  cost: number;
  accumulated: number;
  bookValue: number;
  inCustody: number;
  unassigned: number;
}

/**
 * «من تاريخ / إلى تاريخ» على تاريخ الشراء.
 *
 * طرفٌ وحده يحدّ، واليوم الأخير داخلٌ كلّه، والقيم نصوص سنة-شهر-يوم كما
 * في الرابط فتبقى مع التنقّل كبقية الشروط.
 */
function DateRange({
  from,
  to,
  onChange,
}: {
  from?: string;
  to?: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
}) {
  return (
    <span className="row" style={{ gap: 8 }}>
      <input
        className="input"
        type="date"
        value={from ?? ''}
        max={to || undefined}
        aria-label="من تاريخ"
        title="من تاريخ"
        onChange={(e) => onChange(e.target.value || undefined, to)}
      />
      <input
        className="input"
        type="date"
        value={to ?? ''}
        min={from || undefined}
        aria-label="إلى تاريخ"
        title="إلى تاريخ"
        onChange={(e) => onChange(from, e.target.value || undefined)}
      />
      {(from || to) && (
        <button
          type="button"
          className="btn btn-sm btn-icon btn-ghost"
          aria-label="مسح مدى التاريخ"
          title="مسح مدى التاريخ"
          onClick={() => onChange(undefined, undefined)}
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </span>
  );
}

/**
 * سجل الأصول الثابتة.
 *
 * الغرض ليس قائمة بالأجهزة — ذلك جدول بيانات يكفي — بل مصدر أرقام الإهلاك
 * التي تدخل قائمة الدخل، وسجلّ العهدة الذي يُسأل عنه حين يُفقد شيء.
 */
export function AssetsList() {
  const { atLeast } = useAuth();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [creating, setCreating] = useState(false);

  const canEdit = atLeast('ADMIN');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['assets', query],
    queryFn: () => api<AssetPage>(`/assets${qs(query)}`),
  });

  /* البطاقات على مدى تاريخ الشراء نفسه الذي في القائمة. */
  const range = { from: state.from, to: state.to };
  const summary = useQuery({
    queryKey: ['assets', 'summary', range],
    queryFn: () => api<Summary>(`/assets/summary${qs(range)}`),
  });

  const categories = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () => api<Array<{ id: string; name: string }>>('/assets/categories'),
  });

  const rows = list.data?.items ?? [];
  const s = summary.data;

  /*
   * التصدير: كل الأصول المطابقة للبحث والمدى والحالة والتصنيف والفلاتر، لا
   * الصفحة المعروضة وحدها. مدى التاريخ على تاريخ الشراء كالقائمة.
   */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<AssetRow>('/assets', query, 200);
    const category = categories.data?.find((c) => c.id === state.categoryId)?.name;
    return {
      title: 'الأصول الثابتة',
      sheets: [
        {
          title: 'الأصول الثابتة',
          period: exportPeriod(state.from, state.to),
          sections: [
            {
              columns: [
                { key: 'code', label: 'الكود' },
                { key: 'name', label: 'الأصل' },
                { key: 'serialNumber', label: 'الرقم التسلسلي' },
                { key: 'category', label: 'التصنيف' },
                { key: 'custodian', label: 'في عهدة' },
                { key: 'purchaseDate', label: 'تاريخ الشراء', kind: 'date' },
                { key: 'purchaseCost', label: 'التكلفة', kind: 'money', total: true },
                { key: 'accumulatedDepreciation', label: 'مجمع الإهلاك', kind: 'money', total: true },
                { key: 'bookValue', label: 'القيمة الدفترية', kind: 'money', total: true },
                { key: 'status', label: 'الحالة' },
              ],
              rows: items.map((a) => ({
                code: a.code,
                name: a.name,
                serialNumber: a.serialNumber,
                category: a.category.name,
                custodian: a.custodian?.name ?? 'المخزن',
                purchaseDate: exportDay(a.purchaseDate),
                purchaseCost: a.purchaseCost,
                accumulatedDepreciation: a.accumulatedDepreciation,
                bookValue: a.bookValue,
                status: ASSET_STATUS_MAP[a.status]?.label ?? a.status,
              })),
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: [
              state.status && `الحالة: ${ASSET_STATUS_MAP[state.status as AssetStatus]?.label ?? state.status}`,
              state.categoryId && `التصنيف: ${category ?? '—'}`,
              ...describeFilters(ASSET_FILTERS, filters),
            ],
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="الأصول الثابتة"
        subtitle={list.data ? `${list.data.total} أصلًا` : undefined}
        actions={
          canEdit && (
            <>
              <Link to="/settings/asset-categories" className="btn">
                التصنيفات
              </Link>
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                أصل جديد
              </button>
            </>
          )
        }
      />

      {s && (
        <div className="stats">
          <Stat label="في الخدمة" value={s.count} />
          <Stat label="تكلفة الاقتناء" value={<span className="num">{money(s.cost)}</span>} />
          <Stat
            label="مجمع الإهلاك"
            value={<span className="num neg">{money(s.accumulated)}</span>}
          />
          <Stat
            label="القيمة الدفترية"
            value={<span className="num strong">{money(s.bookValue)}</span>}
          />
          <Stat label="بلا عهدة" value={s.unassigned} />
        </div>
      )}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الكود أو الرقم التسلسلي"
        />

        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label="الحالة"
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
        >
          <option value="">كل الحالات</option>
          {ASSET_STATUSES.map((st) => (
            <option key={st} value={st}>
              {ASSET_STATUS_MAP[st].label}
            </option>
          ))}
        </select>

        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label="التصنيف"
          value={state.categoryId ?? ''}
          onChange={(e) => patch({ categoryId: e.target.value || undefined })}
        >
          <option value="">كل التصنيفات</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <DateRange
          from={state.from}
          to={state.to}
          onChange={(from, to) => patch({ from, to })}
        />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={ASSET_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={ASSET_FILTERS} filters={filters} onChange={setFilters} />

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <FilterScope
        defs={ASSET_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/assets/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الأصل
                  <HeaderFilter colKey="name" />
                </th>
                <th>
                  التصنيف
                  <HeaderFilter colKey="category" />
                </th>
                <th>
                  في عهدة
                  <HeaderFilter colKey="custodian" />
                </th>
                <th>
                  التكلفة
                  <HeaderFilter colKey="purchaseCost" />
                </th>
                <th>
                  مجمع الإهلاك
                  <HeaderFilter colKey="accumulatedDepreciation" />
                </th>
                <th>القيمة الدفترية</th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
              </tr>
            </thead>

            {list.isLoading && <TableSkeleton rows={6} cols={7} />}

            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/assets/${a.id}`} className="cell-strong link">
                      {a.name}
                    </Link>
                    <div className="tiny mute num" dir="ltr">
                      {a.code}
                      {a.serialNumber && ` · ${a.serialNumber}`}
                    </div>
                  </td>
                  <td className="cell-mute">{a.category.name}</td>
                  <td className={a.custodian ? '' : 'cell-mute'}>
                    {a.custodian?.name ?? 'المخزن'}
                  </td>
                  <td className="num">{money(a.purchaseCost)}</td>
                  <td className="num neg">{money(a.accumulatedDepreciation)}</td>
                  <td className="num cell-strong">{money(a.bookValue)}</td>
                  <td>
                    <Badge kind={ASSET_STATUS_MAP[a.status].kind}>
                      {ASSET_STATUS_MAP[a.status].label}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!list.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا أصول مسجَّلة"
            text="سجّل أصولك ليُحسب إهلاكها ويُرحَّل إلى دفاترك، وتُتابَع عهدة كل موظف."
          />
        )}

        {list.data && (
          <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {creating && <AssetDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/* ==========================================================================
   تسجيل أصل
   ========================================================================== */

export function AssetDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState('');
  const [serialNumber, setSerial] = useState('');
  const [location, setLocation] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [purchaseCost, setCost] = useState('');
  const [salvageValue, setSalvage] = useState('');
  const [custodianId, setCustodian] = useState('');
  const [override, setOverride] = useState(false);
  const [method, setMethod] = useState<DepreciationMethod>('STRAIGHT_LINE');
  const [rate, setRate] = useState('');
  const [life, setLife] = useState('');

  const categories = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () =>
      api<
        Array<{
          id: string;
          name: string;
          method: DepreciationMethod;
          rate: number;
          usefulLifeMonths: number;
          isActive: boolean;
        }>
      >('/assets/categories'),
  });

  const employees = useQuery({
    queryKey: ['employees', { pageSize: 200, status: 'ACTIVE' }],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        '/hr/employees?pageSize=200&status=ACTIVE',
      ),
  });

  const chosen = categories.data?.find((c) => c.id === categoryId);

  const create = useMutation({
    mutationFn: () =>
      api('/assets', {
        method: 'POST',
        body: {
          categoryId,
          name: name.trim(),
          serialNumber: serialNumber || null,
          location: location || null,
          purchaseDate,
          purchaseCost: Number(purchaseCost) || 0,
          salvageValue: Number(salvageValue) || 0,
          custodianId: custodianId || null,
          ...(override
            ? {
                method,
                rate: Number(rate) || 0,
                usefulLifeMonths: Number(life) || 1,
              }
            : {}),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      onClose();
    },
  });

  const valid =
    categoryId && name.trim().length >= 2 && Number(purchaseCost) > 0 &&
    Number(salvageValue || 0) < Number(purchaseCost);

  return (
    <Modal
      title="أصل جديد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={!valid || create.isPending}
          >
            {create.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {categories.data?.length === 0 && (
        <div className="alert alert-warning mb-4">
          لا تصنيفات بعد. أضِف تصنيفًا أولًا — التصنيف يحمل نسبة الإهلاك وحسابات الترحيل.
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="a-category">
            التصنيف
          </label>
          <select
            id="a-category"
            className="select"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">اختر</option>
            {categories.data
              ?.filter((c) => c.isActive)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          {chosen && (
            <span className="tiny mute">
              {DEPRECIATION_METHOD_MAP[chosen.method].label} بنسبة {chosen.rate}٪ على{' '}
              {chosen.usefulLifeMonths} شهرًا.
            </span>
          )}
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="a-name">
            اسم الأصل
          </label>
          <input
            id="a-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="a-serial">
            الرقم التسلسلي
          </label>
          <input
            id="a-serial"
            className="input num"
            dir="ltr"
            value={serialNumber}
            onChange={(e) => setSerial(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="a-location">
            الموقع
          </label>
          <input
            id="a-location"
            className="input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="a-date">
            تاريخ الشراء
          </label>
          <input
            id="a-date"
            className="input num"
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
          />
          <span className="tiny mute">الإهلاك يبدأ من شهر الشراء.</span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="a-cost">
            تكلفة الاقتناء
          </label>
          <input
            id="a-cost"
            className="input num"
            dir="ltr"
            inputMode="decimal"
            value={purchaseCost}
            onChange={(e) => setCost(e.target.value)}
          />
          <span className="tiny mute">سعر الشراء وما لزم لتشغيله.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="a-salvage">
            قيمة الخردة
          </label>
          <input
            id="a-salvage"
            className="input num"
            dir="ltr"
            inputMode="decimal"
            value={salvageValue}
            onChange={(e) => setSalvage(e.target.value)}
          />
          {/* لا يُهلك الأصل تحتها مهما طال عمره. */}
          <span className="tiny mute">لا يُهلك الأصل تحت هذه القيمة.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="a-custodian">
            العهدة
          </label>
          <select
            id="a-custodian"
            className="select"
            value={custodianId}
            onChange={(e) => setCustodian(e.target.value)}
          >
            <option value="">المخزن — بلا عهدة</option>
            {employees.data?.items.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <label className="check">
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => {
                setOverride(e.target.checked);
                if (e.target.checked && chosen) {
                  setMethod(chosen.method);
                  setRate(String(chosen.rate));
                  setLife(String(chosen.usefulLifeMonths));
                }
              }}
            />
            <span>سياسة إهلاك خاصة بهذا الأصل</span>
          </label>
          {/*
            السياسة تُنسخ إلى الأصل وقت التسجيل ولا تُقرأ من التصنيف بعدها،
            فتغيير التصنيف لاحقًا لا يمسّ إهلاكًا رُحِّل.
          */}
          <span className="tiny mute">
            الافتراضي سياسة التصنيف، وتُنسخ إلى الأصل فلا تتأثر بتعديل التصنيف لاحقًا.
          </span>
        </div>

        {override && (
          <>
            <div className="field">
              <label className="label" htmlFor="a-method">
                الطريقة
              </label>
              <select
                id="a-method"
                className="select"
                value={method}
                onChange={(e) => setMethod(e.target.value as DepreciationMethod)}
              >
                {DEPRECIATION_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {DEPRECIATION_METHOD_MAP[m].label}
                  </option>
                ))}
              </select>
              <span className="tiny mute">{DEPRECIATION_METHOD_MAP[method].description}</span>
            </div>

            <div className="field">
              <label className="label" htmlFor="a-rate">
                النسبة السنوية ٪
              </label>
              <input
                id="a-rate"
                className="input num"
                dir="ltr"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                disabled={method !== 'DECLINING'}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="a-life">
                العمر الإنتاجي بالشهور
              </label>
              <input
                id="a-life"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={life}
                onChange={(e) => setLife(e.target.value)}
                disabled={method !== 'STRAIGHT_LINE'}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
